/**
 * FanOutUnitRow — one child unit inside a fan-out parent step (Spec 82 S6).
 *
 * SECURITY: only the whitelisted compact fields on the generated FanOutUnit type are
 * read here — unitId, unitType, personaSlug, status, retryCount, startedAt/completedAt,
 * errorSummary, confidence, evidenceRefs, and output.{summary,nextActions,artifactRefs}.
 * Raw child-agent internals (transcript / logs / messages / prompt / systemPrompt /
 * tool inputs+outputs / rawToolCalls) are NEVER read and must never be added — the
 * debugger keeps the root context clean by surfacing summary-only reports.
 */
import React from 'react';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge.js';
import { Chip } from '../../../components/ui/Chip.js';
import { type FanOutUnit, durationBetween, formatDuration, formatTime } from './run-detail-types.js';

function unitStatusVariant(status: FanOutUnit['status']): BadgeVariant {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
      return 'accent';
    case 'skipped':
      return 'neutral';
    default:
      return 'neutral'; // pending
  }
}

interface FanOutUnitRowProps {
  unit: FanOutUnit;
}

export const FanOutUnitRow: React.FC<FanOutUnitRowProps> = ({ unit }) => {
  const duration = durationBetween(unit.startedAt, unit.completedAt);
  const output = unit.output;
  const hasRefs = (output?.artifactRefs.length ?? 0) > 0 || unit.evidenceRefs.length > 0;

  return (
    <li className="px-3 py-2.5 border-b border-border last:border-b-0">
      {/* Identity + status + per-unit meta */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant={unitStatusVariant(unit.status)} dot>
            {unit.status}
          </Badge>
          <span className="text-[12.5px] font-medium text-heading truncate">{unit.personaSlug}</span>
          <Chip tone="neutral">{unit.unitType}</Chip>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10.5px] text-faint shrink-0">
          {unit.retryCount > 0 && <span className="text-warning">retry {unit.retryCount}</span>}
          {unit.confidence != null && <span>conf {Math.round(unit.confidence * 100)}%</span>}
          {duration != null && <span>{formatDuration(duration)}</span>}
        </div>
      </div>

      {/* unitId + timing */}
      <div className="mt-1 flex items-center gap-2 font-mono text-[10px] text-faint flex-wrap">
        <span className="truncate">{unit.unitId}</span>
        {unit.startedAt && <span>· {formatTime(unit.startedAt)}</span>}
        {unit.completedAt && <span>→ {formatTime(unit.completedAt)}</span>}
      </div>

      {/* errorSummary — failed/degraded units */}
      {unit.errorSummary && <p className="mt-1.5 text-[11.5px] text-danger leading-normal">{unit.errorSummary}</p>}

      {/* Compact output — summary only */}
      {output?.summary && <p className="mt-1.5 text-[12px] text-body leading-[1.55]">{output.summary}</p>}

      {output && output.nextActions.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {output.nextActions.map((action, i) => (
            <li key={`na-${i}`} className="text-[11.5px] text-muted leading-[1.45]">
              → {action}
            </li>
          ))}
        </ul>
      )}

      {hasRefs && (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {output?.artifactRefs.map((ref, i) => (
            <Chip key={`ar-${ref}-${i}`} tone="blue">
              {ref}
            </Chip>
          ))}
          {unit.evidenceRefs.map((ref, i) => (
            <Chip key={`er-${ref}-${i}`} tone="teal">
              {ref}
            </Chip>
          ))}
        </div>
      )}
    </li>
  );
};
